<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CategoryCheckbox from '@/components/Inputs/CategoryCheckbox.vue'
import SortRadio from '@/components/Inputs/SortRadio.vue'
import type { Category } from '@/http/models/Category'
import { MarketplaceSortingKey } from '@/enums/MarketplaceSortingKey'
import { useMarketplaceStore } from '@/stores/ssr/useMarketplaceStore'

const { t } = useI18n()
const marketplaceStore = useMarketplaceStore()

const props = defineProps<{
  filterType: string
  dataTestId: string
}>()

const open = ref<boolean>(true)
const isCapacitor = __CAPACITOR__

const data = computed(() => {
  if (props.filterType === 'sorting') {
    return Object.values(MarketplaceSortingKey).map((item) => ({
      name: t(`sort.${item}`),
      value: item,
    }))
  } else if (props.filterType === 'categories') {
    // @todo: marketplace categories :sadge:
    return marketplaceStore.categories?.map((item: Category) => ({
      name: item.name,
      value: item.id,
    }))
  }

  return []
})
</script>

<template>
  <div class="accordion"
       :class="{ 'accordion--open': open || isCapacitor }"
  >
    <div class="accordion__heading"
         :data-test-id="`${dataTestId}-heading`"
         @click="open = !open"
    >
      <div class="accordion__title">
        <slot><!--Accordion title--></slot>
      </div>
    </div>
    <div class="accordion__content">
      <form :data-test-id="`${dataTestId}-content`">
        <ul class="accordion__list">
          <li v-for="item in data" :key="`${item.name}-${item.value}`" class="accordion__list-item">
            <SortRadio name="sortingKey"
                       :data-test-id="`sort-${item.value}`"
                       :value="item.value"
                       v-if="filterType === 'sorting'">
              {{ item.name }}
            </SortRadio>
            <CategoryCheckbox name="filteredCategories"
                              :data-test-id="`category-${item.name}`"
                              :value="item.value"
                              v-if="filterType === 'categories'">
              {{ item.name }}
            </CategoryCheckbox>
          </li>
        </ul>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/interactions.scss";

.accordion {
  $self: &;
  margin: 1.5rem 0;

  &__heading {
    font-size: 1.125rem;
    font-weight: 700;
    padding:   0 4rem 0 0;
    position:  relative;

    #{ $self }__title {
      width:       100%;
      line-height: 1;
      white-space: nowrap;
      text-overflow: ellipsis;
      display:     flex;
      gap:         0.5rem;
    }

    @include lg-up {
      &::after {
        display:  block;
        content:  url("/assets/img/icons/arrow.svg");
        position: absolute;
        right:    0;
        top:      0;
        transition: transform 120ms ease-out;
      }
    }
    @include hover {
      &:hover {
        cursor: pointer;

        &:after {
          transform: scale(1.3);
          transition: transform 120ms ease-out;
        }
      }
    }
  }

  &__content {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.15s ease-out;
    padding:  0;
  }

  &--open {
    #{ $self }__heading {
      #{ $self }__title {
      }

      &::after {
        transform: rotate(180deg);
      }

      @include hover {
        &:hover {
          &:after {
            transform: rotate(180deg) scale(1.3);
          }
        }
      }
    }

    #{ $self }__content {
      max-height: initial;
      padding: 1.5rem 0 0 0;
      transition: max-height 0.25s ease-in;

      #{ $self }__list {
        list-style: none;
        margin:  -1rem 0;
        padding: 0;

        #{ $self }__list-item {
          margin: 1rem 0;
          padding: 0;
        }
      }
    }
  }
}
</style>
